/** @jsx jsx */
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, jsx } from 'theme-ui'
import { MarkerClusterer } from '@googlemaps/markerclusterer'

const StoreLocatorMap = ({
  activeLocation,
  currentCenter,
  distanceThreshold,
  getDistanceFromCurrentCenter,
  locations,
  map,
  mapId,
  setActiveLocation,
  setCurrentCenter,
  setMap,
  styles,
  ui,
}) => {
  const mapContainer = useRef(null)
  const [clusterer, setClusterer] = useState(null)
  const [markers, setMarkers] = useState(null)
  const [tooltips, setTooltips] = useState(null)

  const markerIcons = useMemo(() => {
    const inactive = {
      path: 'M34.2,90h0.3c8.2-12,34.6-33.9,34.2-57.5C68.8,14.7,52.4,0.2,34.5,0v0c-0.1,0-0.2,0-0.3,0v0C16.4,0.2,0,14.7,0,32.5C-0.4,56.1,26,78,34.2,90z',
      fillOpacity: 1,
      strokeColor: 'white',
      strokeWeight: 1,
      scale: 0.5,
      anchor: new window.google.maps.Point(35, 90),
      ...ui?.map?.marker?.icon,
    }
    const active = { ...inactive, ...ui?.map?.marker?.iconActive }
    const cluster = {
      url: `data:image/svg+xml;base64,${window.btoa(`
  <svg fill="${ui?.map?.marker?.icon?.fillColor}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" r="70" stroke="#FFFFFF" stroke-width="4" />
  </svg>`)}`,
    }

    return { active, cluster, inactive }
  }, []) // eslint-disable-line

  // Initializes the map.
  useEffect(() => {
    if (map || !mapContainer.current) return

    setMap(
      new window.google.maps.Map(mapContainer.current, {
        center: currentCenter,
        clickableIcons: false,
        mapId,
        mapTypeControlOptions: {
          mapTypeIds: [],
        },
        streetViewControl: false,
        zoom: 12,
      })
    )
  }, [mapContainer]) // eslint-disable-line

  // Initializes the markers.
  useEffect(() => {
    if (!map || !locations || markers) return

    const newMarkers = []
    const newBounds = new window.google.maps.LatLngBounds()
    let shortestDistance = 10000

    locations.forEach((location) => {
      const marker = new window.google.maps.Marker({
        icon: markerIcons.inactive,
        map,
        position: location.coords,
        title: location.name,
      })
      marker.addListener('click', () => setActiveLocation(location))
      newMarkers[location.id] = marker
      newBounds.extend(location.coords)
      shortestDistance = Math.min(
        getDistanceFromCurrentCenter(location.coords),
        shortestDistance
      )
    })

    setMarkers(newMarkers)

    if (shortestDistance > distanceThreshold) {
      // Show all locations if the user's default location isn't near any.
      map.fitBounds(newBounds)
    } else {
      // Force distance calculation in list.
      setCurrentCenter({ ...currentCenter })
      map.setZoom(12)
    }
  }, [locations, map]) // eslint-disable-line

  // Clusters the markers.
  useEffect(() => {
    if (!map || !markers || clusterer) return

    setClusterer(
      new MarkerClusterer({
        map,
        markers,
        renderer: {
          render: ({ count, position }, stats) => {
            const marker = new window.google.maps.Marker({
              position,
              icon: {
                ...markerIcons.cluster,
                scaledSize: new window.google.maps.Size(
                  Math.min(50 + count, 75),
                  Math.min(50 + count, 75)
                ),
              },
              label: {
                text: String(count),
                color: '#FFFFFF',
                fontSize: '14px',
              },
              zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
            })
            marker.addListener('click', () => setActiveLocation(null))
            return marker
          },
        },
      })
    )
  }, [map, markers]) // eslint-disable-line

  // Initializes the tooltips.
  useEffect(() => {
    if (!map || !locations || tooltips) return

    const newTooltips = []

    locations.forEach((location) => {
      const newTooltip = new window.google.maps.InfoWindow({
        content: `
          <div class="store-locator-map-tooltip">
            <p class="store-locator-map-tooltip-title">${location.title}</p>
            <p class="store-locator-map-tooltip-address">
              ${location.address1}</br>${location.address2}
            </p>
            ${
              location.website
                ? `
            <p class="store-locator-map-tooltip-website">
              <a class="store-locator-map-tooltip-link" href="${location.website}" target="_blank">Store website</a>
            </p>`
                : ''
            }
            <p class="store-locator-map-tooltip-directions">
              <a class="store-locator-map-tooltip-link" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                location.address1 + ', ' + location.address2
              )}" target="_blank">Directions</a>
            </p>
          </div>
        `,
      })
      newTooltip.addListener('closeclick', () => setActiveLocation(null))
      newTooltips[location.id] = newTooltip
    })

    setTooltips(newTooltips)
  }, [locations, map]) // eslint-disable-line

  useEffect(() => {
    if (!map || !markers || !tooltips) return

    // Zooms into a location if it's currently in a cluster.
    if (clusterer && activeLocation?.id !== undefined) {
      clusterer?.clusters.forEach((cluster) => {
        cluster?.markers.forEach((marker) => {
          if (
            marker === markers[activeLocation.id] &&
            cluster.markers.length > 1
          ) {
            map.panTo(marker.position)
            map.setZoom(15)
          }
        })
      })
    }

    // Updates the active marker.
    if (ui?.map?.marker?.iconActive) {
      locations.forEach((location) => {
        const icon =
          location.id === activeLocation?.id
            ? markerIcons.active
            : markerIcons.inactive
        markers[location.id].setIcon(icon)
      })
    }

    // Opens the appropriate tooltip.
    tooltips.forEach((tooltip) => tooltip.close())
    if (activeLocation?.id !== undefined) {
      tooltips[activeLocation.id].open({
        anchor: markers[activeLocation.id],
        map,
        shouldFocus: false,
      })
    }
  }, [activeLocation]) // eslint-disable-line

  // Update the current center as the user drags.
  useEffect(() => {
    if (!map) return

    map.addListener('dragend', () => {
      const center = map.getCenter()

      setCurrentCenter({
        lat: center.lat(),
        lng: center.lng(),
      })
    })
  }, [map]) // eslint-disable-line

  // Changes the map center when a search updates the current center.
  useEffect(() => {
    if (!map || !currentCenter) return

    map.setCenter(currentCenter)
  }, [currentCenter]) // eslint-disable-line

  return (
    <Box
      ref={mapContainer}
      sx={{
        borderRadius: [null, null, '8px'],
        height: ['336px', '480px'],
        maxHeight: ['336px', '480px'],
        overflow: 'hidden',
        '.store-locator-map-tooltip-title': {
          fontWeight: 'bold',
          mb: 0,
          mt: '4px',
        },
        '.store-locator-map-tooltip-address': {
          mb: 0,
          mt: '4px',
        },
        '.store-locator-map-tooltip-website': {
          mb: 0,
          mt: '4px',
        },
        '.store-locator-map-tooltip-directions': {
          my: '4px',
        },
        '.store-locator-map-tooltip-link': {
          fontWeight: 'bold',
          textDecoration: 'none',
        },
        ...styles?.map?.map,
      }}
    />
  )
}

export default StoreLocatorMap
